import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useAgencyAvailability } from "@/hooks/useAgencyAvailability";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import MetaChip from "@/ui/Badges/MetaChip";
import { SheetActionRow } from "@/ui/Clickable/SheetActionRow";
import SystemDialog from "@/ui/Popups/Dialog";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { agencySourceLabel } from "@/utils/agencyIntegrations";
import { agencyScheduleDescription, agencyTargetLabel } from "@/utils/agencyPresentation";
import { type AgencySelectionDraft, resolveAgencySelections } from "@/utils/agencyTargets";
import { NotificationType, RequestStatus, type ShiftRequest } from "@/utils/models";
import { useCanPostRequestToAgency } from "../RequestData/hooks/useCanPostRequestToAgency";
import { usePostRequestToAgency } from "../RequestData/hooks/usePostRequestToAgency";
import { AgencySelectionFields } from "./AgencySelectionFields";
import { AgencyTargetDelayField } from "./AgencyTargetDelayField";

export function MultiAgencyPostControl({ request, variant }: { request: ShiftRequest; variant: "button" | "chip" | "iconToggle" | "row" }) {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { availableAgencies } = useAgencyAvailability();
	const { roleGroups } = useCanPostRequestToAgency(request);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const [open, setOpen] = useState(false);
	const [drafts, setDrafts] = useState<AgencySelectionDraft[]>([]);
	const { mutateAsync: postRequest, isPending } = usePostRequestToAgency();
	if (request.request_type !== NotificationType.OpenShift || !request.agency_targets) return null;
	const targets = request.agency_targets;
	const available = availableAgencies.filter((source) => !targets.some((target) => target.agency_source === source));
	const canAdd = request.status === RequestStatus.Active && available.length > 0 && !targets.some((target) => target.origin === "agency");
	const selections = resolveAgencySelections(drafts);
	const statusChips = targets.map((target) => (
		<MetaChip
			key={target.agency_source}
			label={agencyTargetLabel(target, t)}
			tone={target.state === "failed" ? "error" : target.state === "pending" ? "warning" : target.state === "posted" ? "success" : "neutral"}
			tooltip={
				target.scheduled_post_at
					? agencyScheduleDescription(
							agencySourceLabel(target.agency_source),
							target.lead_time_hours,
							target.scheduled_post_at,
							target.state,
							chosenDepartment.timezone,
							DateTime.now(),
							t,
						)
					: t("agency.multi.counts", { filled: target.filled_count, open: target.open_count })
			}
			onClick={(event) => {
				event.stopPropagation();
				setOpen(true);
			}}
			dataTestId={`agency-status-${request.id}-${target.agency_source}`}
		/>
	));
	if (!canAdd && targets.length === 0) return null;
	return (
		<>
			{variant === "row" ? (
				<SheetActionRow
					icon={<CloudUploadOutlinedIcon />}
					label={t("agency.multi.details")}
					description={targets.map((target) => agencyTargetLabel(target, t)).join("; ")}
					showChevron
					onClick={() => setOpen(true)}
					dataTestId={`agency-details-${request.id}`}
				/>
			) : variant === "chip" ? (
				<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>{statusChips}</Box>
			) : (
				<SystemButton
					iconButton
					leadingIcon={<CloudUploadOutlinedIcon />}
					aria-label={t("agency.multi.details")}
					tooltip={t("agency.multi.details")}
					onClick={(event) => {
						event.stopPropagation();
						setOpen(true);
					}}
					data-testid={`agency-details-${request.id}`}
				/>
			)}
			{open && (
				<SystemDialog
					open
					onClose={() => setOpen(false)}
					title={t("agency.multi.details")}
					dataTestId="multi-agency-dialog"
					confirm={canAdd ? t("agency.multi.add") : undefined}
					hideConfirm={!canAdd}
					confirmDisabled={!canAdd || !selections || selections.length === 0}
					confirmLoading={isPending}
					onConfirm={async () => {
						if (!selections?.length) return;
						await postRequest({ requestId: request.id, agencyTargets: selections });
						setOpen(false);
						setDrafts([]);
					}}
					body={
						<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
							{targets.map((target) => (
								<Box key={target.agency_source}>
									<Typography variant="subtitle2">
										{agencyTargetLabel(target, t)} · {target.role}
									</Typography>
									<Typography variant="body2">{t("agency.multi.counts", { filled: target.filled_count, open: target.open_count })}</Typography>
									{target.has_applicants && <Typography variant="caption">{t("agency.multi.applicants")}</Typography>}
									{target.scheduled_post_at && (
										<Typography variant="caption" component="p">
											{agencyScheduleDescription(
												agencySourceLabel(target.agency_source),
												target.lead_time_hours,
												target.scheduled_post_at,
												target.state,
												chosenDepartment.timezone,
												DateTime.now(),
												t,
											)}
										</Typography>
									)}
									{request.status === RequestStatus.Active && target.lead_time_hours !== null && (target.state === "pending" || target.state === "failed") && (
										<AgencyTargetDelayField
											key={`${target.agency_source}:${target.lead_time_hours}`}
											requestId={request.id}
											source={target.agency_source}
											savedHours={target.lead_time_hours}
										/>
									)}
								</Box>
							))}
							{canAdd && (
								<AgencySelectionFields
									mode="add"
									agencies={available}
									value={drafts}
									onChange={setDrafts}
									jobId={request.job_id}
									roleGroups={roleGroups}
									jobManagementEnabled={jobManagementEnabled}
									dataTestId="multi-agency-selection"
								/>
							)}
						</Box>
					}
				/>
			)}
		</>
	);
}
